/**
 * The selected goal's call plan, and the goal list's rows: pure, shared by the
 * results page (`useExperimentDetailModel`) and the dev-state fixtures.
 *
 * Call plan: sample size from the existing power calculation for THIS goal's
 * baseline (`assessTestHealth` → `requiredSamples`), the test type's minimum
 * run, and the Bayesian chance the leading version beats Control. Same rule
 * for every goal; `callDecision` (resultsStage.ts) adds settling and health.
 */

import { assessTestHealth, TEST_TYPE_CONFIGS } from "@/lib/utils/abTestStatistics"
import { formatChance } from "@/lib/experiments/bayes"
import { callGates, type CallGate } from "@/lib/experiments/resultsStage"
import {
  CALL_CHANCE,
  compareGoal,
  formatChange,
  formatGoalRate,
  selectGoalMetric,
  type ClickSetTarget,
  type GoalCandidate,
  type GoalComparison,
  type GoalContext,
  type GoalMetricResult,
  type VariantSource,
} from "@/lib/experiments/goalMetric"

const MDE_PERCENT = 20

export interface CallPlan {
  /** Total visitors across every version the power calculation asks for; null for descriptive goals. */
  requiredSamples: number | null
  minDays: number
  /** Days to the required sample at today's traffic. */
  estimatedTotalDays: number | null
  /** The later of the minimum run and the sample estimate; null when unknown. */
  earliestCallDay: number | null
  gates: CallGate[]
  /** The leader's confidence: P(beats Control), or P(best of all) with 3+ versions. */
  chance: number
}

export function goalCallPlan({
  result,
  comparison,
  daysRunning,
  dailyVisitors,
  isComponentTest,
  dispersion = 1,
}: {
  result: GoalMetricResult
  comparison: GoalComparison
  daysRunning: number
  /** Visitors a day across versions. */
  dailyVisitors: number
  isComponentTest: boolean
  dispersion?: number
}): CallPlan {
  const testType = isComponentTest ? "engagement" : "conversion"
  const defaultMin = TEST_TYPE_CONFIGS[testType].minDuration
  const { control, focus } = comparison
  // Confidence: P(beats Control) with two versions, P(best of all) with more.
  const chance = focus?.best ?? 0.5
  if (!control || !focus || !result.available || result.metric === "descriptive") {
    return { requiredSamples: null, minDays: defaultMin, estimatedTotalDays: null, earliestCallDay: null, gates: [], chance }
  }
  const health = assessTestHealth({
    controlConversions: control.events,
    controlSamples: control.exposure,
    variantConversions: focus.events,
    variantSamples: focus.exposure,
    daysRunning,
    dailyVisitors,
    baselineRate: control.rate > 0 ? Math.min(control.rate, 0.99) : 0.03,
    mdePercent: MDE_PERCENT,
    targetSignificance: 95,
    testType,
    metric: result.metric === "count" ? "count" : "proportion",
    dispersion,
  })
  const minDays = health.recommendedMinDays
  // The power calculation sizes Control + one challenger; every version needs
  // the same per-version sample, and dailyVisitors is across all versions.
  const versions = Math.max(2, comparison.rows.length)
  const requiredSamples = Math.ceil((health.requiredSamples / 2) * versions)
  const totalSessions = comparison.rows.reduce((sum, r) => sum + r.exposure, 0)
  const estimatedTotalDays = dailyVisitors > 0 ? Math.max(daysRunning, Math.ceil(requiredSamples / dailyVisitors)) : null
  const gates = callGates({
    totalSessions,
    requiredSamples,
    daysRunning,
    minDays,
    chance,
    targetChance: CALL_CHANCE,
    estimatedTotalDays,
    formatNumber: (n) => n.toLocaleString("en-US"),
  })
  return {
    requiredSamples,
    minDays,
    estimatedTotalDays,
    earliestCallDay: Math.max(minDays, estimatedTotalDays ?? minDays),
    gates,
    chance,
  }
}

export interface EvaluatedGoal {
  candidate: GoalCandidate
  result: GoalMetricResult
  comparison: GoalComparison
}

/** Numbers for every row: each candidate on the sources that carry its data. */
export function evaluateGoals({
  candidates,
  ctx,
  sourcesFor,
  dispersion = 1,
  nameOf,
  unavailableReason,
}: {
  candidates: GoalCandidate[]
  ctx: GoalContext
  /** The sources holding a candidate's data (e.g. its own clickSet request). */
  sourcesFor: (candidate: GoalCandidate) => VariantSource[]
  dispersion?: number
  nameOf?: (t: ClickSetTarget) => string
  /** Overrides the reason on rows without data (e.g. "Only collected when it is the launch goal"). */
  unavailableReason?: string | null
}): Map<string, EvaluatedGoal> {
  const out = new Map<string, EvaluatedGoal>()
  for (const candidate of candidates) {
    let result = selectGoalMetric(candidate.goal, ctx, sourcesFor(candidate), { isLaunch: candidate.isLaunch, nameOf })
    if (!result.available && !candidate.isLaunch && unavailableReason) result = { ...result, reason: unavailableReason }
    out.set(candidate.key, { candidate, result, comparison: compareGoal(result, { dispersion }) })
  }
  return out
}

export interface GoalRowSummary {
  key: string
  label: string
  isPrimary: boolean
  isLaunch: boolean
  isDraft?: boolean
  isClicks: boolean
  available: boolean
  reason?: string
  /** "3.0% → 4.5%" (Control → the leading version), or null. */
  rates: string | null
  focusLabel: string | null
  change: string | null
  /** Formatted confidence ("93%"), null for descriptive goals or no data. */
  chance: string | null
  /** Raw confidence 0..1 for tone; null like `chance`. */
  confidence: number | null
  /** The leader's likely range and observed change, in percent, for the row's bar. */
  range: { lower: number; upper: number; point: number } | null
}

/** One compact row per goal: Control → leader, change, confidence, likely range. */
export function goalRows(evaluated: Map<string, EvaluatedGoal>, primaryKey: string): GoalRowSummary[] {
  return [...evaluated.values()].map(({ candidate: c, result, comparison }) => {
    const { focus, control } = comparison
    const has = Boolean(result.available && comparison.hasData && focus && control)
    // 0 vs 0 says nothing: no confidence number for it.
    const empty = has && focus!.events === 0 && control!.events === 0
    const fr = (r: number) => formatGoalRate(r, result.unit, result.currency)
    const confidence = has && !empty && focus!.best != null ? focus!.best : null
    return {
      key: c.key,
      label: result.label,
      isPrimary: c.key === primaryKey,
      isLaunch: c.isLaunch,
      isDraft: c.isDraft,
      isClicks: c.goal?.type === "clicks",
      available: result.available,
      reason: result.reason,
      rates: has ? `${fr(control!.rate)} → ${fr(focus!.rate)}` : null,
      focusLabel: focus?.label ?? null,
      change: has ? formatChange(focus!.change, focus!.isNew) : null,
      chance: confidence != null ? formatChance(confidence) : null,
      confidence,
      range: has && !empty && focus!.range && focus!.change != null ? { ...focus!.range, point: focus!.change } : null,
    }
  })
}
